import { useCallback, useState } from 'react';
import { DoorOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AdminRequestError, setAllPoolsOpenToAll, type AdminPool } from './api';
import { countPoolsOpenToAll, poolAccessResultMessage } from './board-state';
import { useModalDismiss } from './use-modal-dismiss';

// Floor-wide pool access: opens every pool to every SDR, or restricts them all back to
// their rosters. Reached when the pools an SDR is assigned to are all dry, closed or
// asleep, so that they get something to dial rather than idling until someone edits
// assignments. `pools` is passed in rather than fetched, so the count cannot contradict
// what is already on screen; undefined means not loaded.
export function OpenAllPoolsButton({
  pools,
  onDone,
}: {
  pools: AdminPool[] | undefined;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  // Stable, because useModalDismiss re-runs on a new identity and would pull focus.
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <DoorOpen />
        Pool access
      </Button>
      {open && <OpenAllPoolsModal pools={pools} onDone={onDone} onClose={close} />}
    </>
  );
}

function OpenAllPoolsModal({
  pools,
  onDone,
  onClose,
}: {
  pools: AdminPool[] | undefined;
  onDone: () => void;
  onClose: () => void;
}) {
  const dialog = useModalDismiss(onClose);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  function apply(openToAll: boolean) {
    setBusy(true);
    setError(null);
    setResult(null);
    setAllPoolsOpenToAll(openToAll)
      .then(({ evicted }) => {
        setResult(poolAccessResultMessage(openToAll, evicted));
        onDone();
      })
      .catch((err: unknown) => {
        setError(err instanceof AdminRequestError ? err.message : 'Failed to save');
      })
      .finally(() => setBusy(false));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        {...dialog}
        aria-label="Pool access"
        className="bg-card flex max-h-[90vh] w-[min(30rem,94vw)] flex-col gap-4 overflow-y-auto rounded-lg border p-5 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-semibold">Pool access</h2>
          <p className="text-muted-foreground text-sm">
            Who may join a pool: only the SDRs assigned to it, or anyone. Takes effect on the next
            picker refresh — no deploy.
          </p>
        </div>

        <OpenToAllCount pools={pools} />

        <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
          <li>
            <strong className="text-foreground">Open all pools to every SDR</strong> puts every pool
            that isn’t hidden into every SDR’s picker, including pools they were never assigned.
          </li>
          <li>
            <strong className="text-foreground">Restrict all pools to assigned SDRs</strong> returns
            them all to their rosters, and sends anyone who was only there because it was open back
            to the picker. Live conversations finish first.
          </li>
          <li>
            Neither changes when a pool can dial: hours, pauses and finished pools still apply.
          </li>
        </ul>

        {error && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            {error}
          </div>
        )}
        {result && <p className="text-sm">{result}</p>}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => apply(false)}>
            Restrict all pools to assigned SDRs
          </Button>
          <Button size="sm" disabled={busy} onClick={() => apply(true)}>
            Open all pools to every SDR
          </Button>
        </div>
      </div>
    </div>
  );
}

// A count of zero is a real answer, so it must never stand in for "not loaded" — the
// levers below are floor-wide, and an operator reading a false zero presses the wrong one.
function OpenToAllCount({ pools }: { pools: AdminPool[] | undefined }) {
  if (!pools) {
    return <p className="text-muted-foreground text-sm">Pool state hasn’t loaded.</p>;
  }

  const n = countPoolsOpenToAll(pools);
  return (
    <div className="bg-muted/40 rounded-md px-3 py-2">
      <p className="text-sm">
        {n === 0 ? (
          'No pools are open to every SDR.'
        ) : (
          <>
            <b>{n}</b> {n === 1 ? 'pool is' : 'pools are'} open to every SDR.
          </>
        )}
      </p>
      <p className="text-muted-foreground text-xs">
        Counting pools an SDR is allowed to join — not hidden, past draft and not finished. Whether
        one can dial at this hour is its calling window’s business.
      </p>
    </div>
  );
}
